import { useMemo } from "react";

import type { SlotEntry } from "./module";
import { usePanel, usePermissions } from "./session";

/** The entries installed modules added to `slot` in this panel, allowed for the user, in order. */
export function useSlot<Props = Record<string, unknown>>(slot: string): SlotEntry<Props>[] {
  const { panel, modules, installed } = usePanel();
  const { can } = usePermissions();
  return useMemo(
    () =>
      modules
        .filter((module) => installed.has(module.name))
        .flatMap((module) => (module[panel]?.slots?.[slot] ?? []) as SlotEntry<Props>[])
        .filter((entry) => !entry.permission || can(entry.permission))
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
    [modules, installed, panel, slot, can],
  );
}

/**
 * Well-known slot `vendor.picker`, provided by the vendors module: a search box to choose one vendor. Modules that
 * keep vendor uids (commission rules, coupons…) render it when installed and fall back to a uid text field.
 */
export const VENDOR_PICKER = "vendor.picker";

export interface VendorPickerProps {
  id?: string;
  /** The chosen vendor's uid (null = none). */
  value: string | null;
  onChange: (uid: string | null) => void;
  invalid?: boolean;
}

/**
 * Well-known slot `vendor.onboarding` (vendor panel), provided by the vendors module: the whole onboarding screen
 * (application wizard and status) shown to logged-in users until their vendor is approved.
 */
export const VENDOR_ONBOARDING = "vendor.onboarding";

/**
 * Tabs other modules add to the admin's customer page (orders, tickets, "My car"…):
 * `admin.slots[CUSTOMER_TABS] = [{ id, label, permission, component: (props: CustomerTabProps) => … }]`.
 */
export const CUSTOMER_TABS = "customer.tabs";

export interface CustomerTabProps {
  customerUid: string;
}

/**
 * Documents and actions other modules add to one vendor order (store part) on the order pages of the admin and
 * vendor panels, e.g. the invoices module's "Download invoice":
 * `admin.slots[VENDOR_ORDER_DOCUMENTS] = [{ id, permission, component: (props: VendorOrderDocumentsProps) => … }]`.
 */
export const VENDOR_ORDER_DOCUMENTS = "orders.vendor_order.documents";

export interface VendorOrderDocumentsProps {
  /** The vendor order's number, e.g. MM26-0000123-A. */
  vendorOrderNumber: string;
  /** Its status: documents usually exist only once it has shipped. */
  status: string;
}
